import { AppState, Asset, Bucket, AssetClassMeta, Liability, LiabilityRepayment, FundTargetRow, UserProfile, MoneyTransaction, AssetHolding } from '../types';
import { INITIAL_DATA, DEFAULT_ASSET_CLASSES } from './initialData';
import { supabase } from '../lib/supabaseClient';

/** Kiểm tra chuỗi có phải UUID hợp lệ không (để lọc slug-id khỏi các cột UUID trong DB) */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isUUID(s: string | undefined | null): boolean {
  return !!s && UUID_RE.test(s);
}

// ── State trống cho user mới (multi-tenant safe) ───────────────────────────────
const RUONG_CHUA_DEFAULT: Bucket = {
  id: 'ruong-chua', name: 'Danh mục Chung',
  description: 'Danh mục mặc định chứa tài sản chưa được phân bổ vào danh mục cụ thể',
  targetValue: 0, color: '#64748b', icon: 'inbox',
};

const EMPTY_STATE: AppState = {
  ...INITIAL_DATA,          // giữ cấu trúc và cashFlowEvents rỗng
  assets:            [],
  buckets:           [RUONG_CHUA_DEFAULT],
  assetClasses:      DEFAULT_ASSET_CLASSES,
  liabilities:       [],
  repayments:        [],
  transactions:      [],
  moneyTransactions: [],
  priceHistory:      [],
  monthlyExpenses:   [],
  holdings:          [],
  lastUpdated:       new Date().toISOString(),
};

// ── Lỗi chi tiết → CustomEvent → App.tsx banner ───────────────────────────────
function notifyError(source: string, error: any) {
  const msg     = error?.message ?? 'Lỗi không xác định';
  const details = error?.details ?? '';
  const hint    = error?.hint    ?? '';
  const code    = error?.code    ?? '';

  // Log đầy đủ vào console để inspect Network tab
  console.error(`[Supabase/${source}]`, {
    message: msg, details, hint, code,
    fullError: error,
  });

  // Phân loại lỗi
  const isAuthError = code === '42501'
    || msg.toLowerCase().includes('row-level security')
    || msg.toLowerCase().includes('permission denied')
    || String(error?.status) === '403';

  const isSchemaCacheError = code === 'PGRST204'
    || msg.toLowerCase().includes('schema cache')
    || msg.toLowerCase().includes('could not find the')
    || msg.toLowerCase().includes('column')
    || (details ?? '').toLowerCase().includes('schema cache');

  const isSessionError = msg.toLowerCase().includes('jwt')
    || msg.toLowerCase().includes('invalid token')
    || msg.toLowerCase().includes('not authenticated')
    || String(error?.status) === '401';

  let displayMessage = msg;
  let displayDetails = details;
  let displayHint    = hint;

  if (isSessionError) {
    displayMessage = 'Phiên làm việc hết hạn hoặc chưa đăng nhập.';
    displayDetails = 'Token xác thực không hợp lệ hoặc đã hết hạn.';
    displayHint    = 'Vui lòng đăng xuất và đăng nhập lại để làm mới phiên.';
  } else if (isAuthError) {
    displayMessage = `Lỗi quyền truy cập hoặc phiên làm việc hết hạn (${source}).`;
    displayDetails = 'Row Level Security từ chối thao tác này.';
    displayHint    = 'Đăng xuất và đăng nhập lại, hoặc kiểm tra RLS policy trong Supabase Dashboard.';
  } else if (isSchemaCacheError) {
    displayMessage = `Lỗi Schema Cache — cột hoặc bảng không tồn tại (${source}).`;
    displayDetails = msg;
    displayHint    = 'Kiểm tra lại cấu trúc bảng trong Supabase → Table Editor. Có thể cần reload ứng dụng.';
  }

  window.dispatchEvent(new CustomEvent('wealthos:sync-error', {
    detail: {
      source,
      message: displayMessage,
      details: displayDetails,
      hint:    displayHint,
      isAuthError: isAuthError || isSessionError,
    },
  }));
}

// ── Lấy user_id hiện tại từ session ───────────────────────────────────────────
export async function getSessionUserId(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.id ?? null;
}

// ── Workspace helpers ──────────────────────────────────────────────────────────

/**
 * Lấy workspace_id của user hiện tại.
 * Self-healing: nếu workspace chưa tồn tại (trigger chưa chạy hoặc lỗi),
 * tự tạo workspace + profile để đảm bảo FK không bao giờ bị vi phạm.
 */
export async function getWorkspaceId(): Promise<string | null> {
  // 1. Thử đọc workspace hiện có
  const { data, error } = await supabase
    .from('workspaces')
    .select('id')
    .single();

  if (data?.id) return data.id as string;

  // 2. Không tìm thấy — tự tạo (trigger có thể chưa chạy hoặc lỗi silently)
  console.warn('[Workspace] Không tìm thấy workspace, đang tự tạo...', error?.message);
  const userId = await getSessionUserId();
  if (!userId) return null;

  const { data: newWs, error: createErr } = await supabase
    .from('workspaces')
    .upsert({ owner_id: userId }, { onConflict: 'owner_id' })
    .select('id')
    .single();

  if (createErr || !newWs) {
    console.error('[Workspace] Không thể tạo workspace:', createErr?.message);
    return null;
  }

  // 3. Đảm bảo profile cũng tồn tại
  await supabase
    .from('user_profiles')
    .upsert(
      { user_id: userId, workspace_id: newWs.id, onboarding_status: 'pending' },
      { onConflict: 'user_id' }
    );

  console.info('[Workspace] Đã tạo workspace mới:', newWs.id);
  return newWs.id as string;
}

/** Lấy profile onboarding của user hiện tại. */
export async function getUserProfile(): Promise<UserProfile | null> {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('user_id, workspace_id, risk_appetite_score, onboarding_status')
    .single();
  if (error || !data) {
    console.warn('[Profile] Không tìm thấy profile:', error?.message);
    return null;
  }
  return {
    userId:            data.user_id,
    workspaceId:       data.workspace_id,
    riskAppetiteScore: data.risk_appetite_score ?? null,
    onboardingStatus:  data.onboarding_status as 'pending' | 'completed',
  };
}

/**
 * Lưu kết quả onboarding (điểm rủi ro) và đánh dấu hoàn thành.
 * Dùng upsert thay vì update để xử lý trường hợp profile chưa tồn tại.
 */
export async function completeOnboarding(score: number): Promise<boolean> {
  const userId = await getSessionUserId();
  if (!userId) return false;

  // Lấy workspace_id hiện tại (tạo nếu chưa có)
  const workspaceId = await getWorkspaceId();

  const { error } = await supabase
    .from('user_profiles')
    .upsert(
      {
        user_id:             userId,
        workspace_id:        workspaceId,
        risk_appetite_score: score,
        onboarding_status:   'completed',
        updated_at:          new Date().toISOString(),
      },
      { onConflict: 'user_id' }
    );
  if (error) { console.error('[Profile] completeOnboarding lỗi:', error); return false; }
  return true;
}

// ── Mapping: Supabase row ↔ App types ─────────────────────────────────────────

function dbRowToAsset(row: any): Asset {
  const joinedClass = row.asset_classes && !Array.isArray(row.asset_classes)
    ? row.asset_classes
    : null;
  const className = joinedClass?.name ?? 'Khác';
  const classId   = row.class_id ? String(row.class_id) : undefined;

  const currentValue = row.value      != null ? Number(row.value)      : 0;
  const costBasis    = row.cost_basis  != null ? Number(row.cost_basis)  : currentValue;

  return {
    id:           String(row.id),
    name:         row.name      ?? '',
    code:         row.code      ?? '',
    assetClass:   className,
    assetClassId: classId,
    isComposite:  row.is_composite === true,
    purchaseDate: new Date().toISOString().split('T')[0],
    costPrice:    costBasis,
    quantity:     1,
    currentPrice: currentValue,
    currency:     'VND',
    bucketId:     row.fund_id ? String(row.fund_id) : 'ruong-chua',
    notes:        row.notes ?? '',
    yieldRate:       row.yield_rate           != null ? Number(row.yield_rate)           : undefined,
    estimatedIncome: row.estimated_income     != null ? Number(row.estimated_income)     : undefined,
    capitalGainRate: row.cap_gain_expectancy  != null ? Number(row.cap_gain_expectancy)  : undefined,
    incomeEvents:    Array.isArray(row.income_events) ? row.income_events : [],
  };
}

function assetToDbRow(a: Asset, userId: string | null, workspaceId: string | null) {
  const row: Record<string, any> = {
    id:           String(a.id),
    name:         a.name       || '(chưa đặt tên)',
    code:         a.code       || '',
    fund_id:      a.bucketId || 'ruong-chua',
    class_id:     isUUID(a.assetClassId) ? a.assetClassId : null,   // UUID FK — bỏ qua slug
    notes:        a.notes ?? '',
    is_composite: a.isComposite ?? false,
    // Composite assets: DB trigger sẽ overwrite sau khi holdings thay đổi.
    // Vẫn phải ghi để thoả mãn NOT NULL constraint — dùng giá trị hiện tại từ state.
    value:      (a.currentPrice ?? 0) * (a.quantity ?? 1),
    cost_basis: (a.costPrice    ?? 0) * (a.quantity ?? 1),
  };
  if (a.yieldRate       != null) row.yield_rate          = a.yieldRate;
  if (a.estimatedIncome != null) row.estimated_income    = a.estimatedIncome;
  if (a.capitalGainRate != null) row.cap_gain_expectancy = a.capitalGainRate;
  row.income_events = a.incomeEvents ?? [];
  if (userId)      row.user_id      = userId;
  if (workspaceId) row.workspace_id = workspaceId;
  return row;
}

function dbRowToAssetClass(row: any): AssetClassMeta {
  return {
    id:           String(row.id),
    name:         row.name           ?? '',
    color:        row.color          ?? '#94a3b8',
    isFixedIncome: row.is_fixed_income ?? false,
  };
}

function assetClassToDbRow(c: AssetClassMeta, userId: string | null, workspaceId: string | null) {
  const row: Record<string, any> = {
    id:              String(c.id),
    name:            c.name  || '(chưa đặt tên)',
    color:           c.color || '#94a3b8',
    is_fixed_income: c.isFixedIncome ?? false,
  };
  if (userId)      row.user_id      = userId;
  if (workspaceId) row.workspace_id = workspaceId;
  return row;
}

function dbRowToBucket(row: any): Bucket {
  return {
    id:             String(row.id),
    name:           row.name            ?? '',
    description:    row.description     ?? '',
    targetValue:    row.target_value    ?? 0,
    color:          row.color           ?? '#475569',
    icon:           row.icon            ?? 'inbox',
    spendingPerYear: row.spending_per_year != null ? Number(row.spending_per_year) : undefined,
    expectedReturn:  row.expected_return   != null ? Number(row.expected_return)   : undefined,
    inflationRate:   row.inflation_rate    != null ? Number(row.inflation_rate)    : undefined,
    horizonYears:    row.horizon_years     != null ? Number(row.horizon_years)     : undefined,
    strategyType:    row.strategy_type     ?? undefined,
  };
}

function bucketToDbRow(b: Bucket, userId: string | null, workspaceId: string | null) {
  const row: Record<string, any> = {
    id:           String(b.id),
    name:         b.name        || '(chưa đặt tên)',
    description:  b.description || '',
    target_value: b.targetValue ?? 0,
    color:        b.color       || '#475569',
    icon:         b.icon        || 'inbox',
  };
  if (b.spendingPerYear != null) row.spending_per_year = b.spendingPerYear;
  if (b.expectedReturn  != null) row.expected_return   = b.expectedReturn;
  if (b.inflationRate   != null) row.inflation_rate    = b.inflationRate;
  if (b.horizonYears    != null) row.horizon_years     = b.horizonYears;
  if (b.strategyType    != null) row.strategy_type     = b.strategyType;
  if (userId)      row.user_id      = userId;
  if (workspaceId) row.workspace_id = workspaceId;
  return row;
}

function dbRowToLiability(row: any, repaymentRows: any[]): Liability {
  // Cột đã đổi tên: original_amount → amount (schema mới)
  const originalBalance = Number(row.amount ?? row.original_amount ?? 0);
  const totalPaid = repaymentRows
    .filter(r => String(r.liability_id) === String(row.id))
    .reduce((s, r) => s + Number(r.principal_paid || 0), 0);
  return {
    id:                 String(row.id),
    name:               row.name           ?? '',
    lender:             row.lender         ?? '',
    type:               row.category       ?? 'Khác',
    originalBalance,
    outstandingBalance: Math.max(0, originalBalance - totalPaid),
    interestRate:       Number(row.interest_rate ?? 0),
    startDate:          row.start_date     ?? '',
    maturityDate:       row.maturity_date  ?? '',
    monthlyPayment:     0,
    linkedAssetId:      row.linked_asset_id ? String(row.linked_asset_id) : undefined,
    fundId:             row.fund_id ? String(row.fund_id) : undefined,
    notes:              '',
  };
}

function liabilityToDbRow(l: Liability, userId: string | null, workspaceId: string | null) {
  const row: Record<string, any> = {
    id:              String(l.id),
    name:            l.name          || '(chưa đặt tên)',
    original_amount: l.originalBalance ?? 0,
    interest_rate:   l.interestRate   ?? 0,
    lender:          l.lender         || '',
    start_date:      l.startDate      || null,
    maturity_date:   l.maturityDate   || null,
    linked_asset_id: l.linkedAssetId  ?? null,
    fund_id:         l.fundId         ?? null,
    category:        l.type           || 'Khác',
  };
  if (userId)      row.user_id      = userId;
  if (workspaceId) row.workspace_id = workspaceId;
  return row;
}

function dbRowToRepayment(row: any): LiabilityRepayment {
  return {
    id:            String(row.id),
    liabilityId:   String(row.liability_id),
    date:          row.date            ?? '',
    principalPaid: Number(row.principal_paid ?? 0),
    interestPaid:  Number(row.interest_paid  ?? 0),
    notes:         row.notes           ?? '',
  };
}

function repaymentToDbRow(r: LiabilityRepayment, userId: string | null, workspaceId: string | null) {
  const row: Record<string, any> = {
    id:             String(r.id),
    liability_id:   String(r.liabilityId),
    date:           r.date         || new Date().toISOString().split('T')[0],
    principal_paid: r.principalPaid ?? 0,
    interest_paid:  r.interestPaid  ?? 0,
    notes:          r.notes         || '',
  };
  if (userId)      row.user_id      = userId;
  if (workspaceId) row.workspace_id = workspaceId;
  return row;
}

// ── AssetHolding mappers ───────────────────────────────────────────────────────

function dbRowToHolding(row: any): AssetHolding {
  return {
    id:          String(row.id),
    assetId:     String(row.asset_id),
    symbol:      row.symbol       ?? '',
    name:        row.name         ?? '',
    quantity:    Number(row.quantity     ?? 0),
    costPrice:   Number(row.cost_price   ?? 0),
    marketPrice: Number(row.market_price ?? 0),
    provider:    row.provider     ?? 'manual',
    lastUpdated: row.last_updated ?? new Date().toISOString(),
    user_id:     row.user_id      ?? undefined,
    workspace_id: row.workspace_id ?? undefined,
  };
}

function holdingToDbRow(h: AssetHolding, userId: string | null, workspaceId: string | null) {
  const row: Record<string, any> = {
    id:           String(h.id),
    asset_id:     String(h.assetId),
    symbol:       h.symbol,
    name:         h.name,
    quantity:     h.quantity,
    cost_price:   h.costPrice,
    market_price: h.marketPrice,
    provider:     h.provider,
    last_updated: h.lastUpdated,
  };
  if (userId)      row.user_id      = userId;
  if (workspaceId) row.workspace_id = workspaceId;
  return row;
}

// ── AssetHolding CRUD ──────────────────────────────────────────────────────────

/** Upsert một holding (tạo mới nếu chưa có, cập nhật nếu đã có). */
export async function saveHolding(holding: AssetHolding): Promise<boolean> {
  const [userId, workspaceId] = await Promise.all([
    getSessionUserId(),
    getWorkspaceId(),
  ]);
  if (!userId || !workspaceId) return false;

  const row = holdingToDbRow(holding, userId, workspaceId);
  const { error } = await supabase
    .from('asset_holdings')
    .upsert(row, { onConflict: 'id' });

  if (error) { notifyError('lưu holding', error); return false; }
  return true;
}

/** Xóa một holding theo id. */
export async function deleteHolding(holdingId: string): Promise<boolean> {
  const { error } = await supabase
    .from('asset_holdings')
    .delete()
    .eq('id', holdingId);

  if (error) { notifyError('xóa holding', error); return false; }
  return true;
}

/**
 * Batch update market_price cho nhiều holdings theo symbol.
 * Gọi bởi price update service sau khi fetch giá.
 */
export async function batchUpdateHoldingPrices(
  updates: { symbol: string; marketPrice: number; provider: string }[]
): Promise<void> {
  if (!updates.length) return;
  const now = new Date().toISOString();
  await Promise.allSettled(
    updates.map(u =>
      supabase
        .from('asset_holdings')
        .update({ market_price: u.marketPrice, provider: u.provider, last_updated: now })
        .eq('symbol', u.symbol)
    )
  );
}

// ── Cloud persistence ──────────────────────────────────────────────────────────

/**
 * Tải state từ Supabase. Trả về null nếu fetch thất bại (lỗi mạng/RLS) để caller
 * có thể giữ nguyên state hiện tại thay vì ghi đè bằng state rỗng.
 */
export async function loadStateFromCloud(): Promise<AppState | null> {
  try {
    let dbAssets:         any[] = [];
    let dbFunds:          any[] = [];
    let dbClasses:        any[] = [];
    let dbLiabilities:    any[] = [];
    let dbRepayments:     any[] = [];
    let dbMoneyTxs:       any[] = [];
    let criticalFetchError = false; // true nếu funds/assets fetch thất bại (RLS hoặc network)

    try {
      // Hint !class_id — chỉ định rõ FK column để tránh lỗi Schema Cache
      // khi PostgREST không tự phát hiện được quan hệ sau khi đổi tên cột
      const { data, error } = await supabase.from('assets').select('*, asset_classes!class_id(*)');
      if (error) { notifyError('đọc assets', error); criticalFetchError = true; }
      else dbAssets = data ?? [];
    } catch (e) { console.error('[Supabase] fetch assets exception:', e); criticalFetchError = true; }

    try {
      const { data, error } = await supabase.from('funds').select('*');
      if (error) {
        notifyError('đọc funds', error);
        console.error('Fetch Error:', error);
        criticalFetchError = true;
      } else {
        dbFunds = data ?? [];
        console.log('Dữ liệu Quỹ từ DB:', dbFunds);
      }
    } catch (e) { console.error('[Supabase] fetch funds exception:', e); criticalFetchError = true; }

    try {
      const { data, error } = await supabase.from('asset_classes').select('*');
      if (error) notifyError('đọc asset_classes', error);
      else dbClasses = data ?? [];
    } catch (e) { console.error('[Supabase] fetch asset_classes exception:', e); }

    try {
      const { data, error } = await supabase.from('liabilities').select('*, assets(id, name, code)');
      if (error) notifyError('đọc liabilities', error);
      else dbLiabilities = data ?? [];
    } catch (e) { console.error('[Supabase] fetch liabilities exception:', e); }

    try {
      const { data, error } = await supabase.from('liability_repayments').select('*');
      if (error) notifyError('đọc liability_repayments', error);
      else dbRepayments = data ?? [];
    } catch (e) { console.error('[Supabase] fetch liability_repayments exception:', e); }

    // Tải money transactions (non-critical — bảng có thể chưa tồn tại)
    try {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .order('date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) {
        // Bảng chưa tồn tại (migration chưa chạy) → bỏ qua lỗi hoàn toàn
        const isMissing = error.code === '42P01' || error.code === 'PGRST200'
          || (error.message ?? '').toLowerCase().includes("transactions");
        if (!isMissing) notifyError('đọc transactions', error);
        // isMissing: dbMoneyTxs giữ nguyên []
      } else {
        dbMoneyTxs = data ?? [];
      }
    } catch (e) { console.error('[Supabase] fetch transactions exception:', e); }

    console.log('[Supabase] Data loaded:', {
      assets: dbAssets.length, funds: dbFunds.length, classes: dbClasses.length,
      liabilities: dbLiabilities.length, repayments: dbRepayments.length,
      criticalFetchError,
    });

    // Nếu fetch lỗi — trả về null để caller giữ nguyên state hiện tại
    if (criticalFetchError) {
      console.warn('[Supabase] Fetch thất bại — trả về null để bảo toàn state.');
      return null;
    }

    // DB thực sự trống (user mới lần đầu đăng nhập) → trả về state trống
    // KHÔNG dùng INITIAL_DATA để tránh seed dữ liệu cứng vào tài khoản người dùng khác
    if (dbAssets.length === 0 && dbFunds.length === 0 && dbClasses.length === 0) {
      console.log('[Supabase] DB trống — user mới, khởi tạo state rỗng');
      return EMPTY_STATE;
    }

    // Load quỹ từ DB và đảm bảo 'ruong-chua' luôn hiện diện (có thể là DB record hoặc fallback)
    const dbBuckets = dbFunds.map(dbRowToBucket);
    const RUONG_CHUA: Bucket = {
      id: 'ruong-chua', name: 'Danh mục Chung',
      description: 'Danh mục mặc định chứa tài sản chưa được phân bổ vào danh mục cụ thể',
      targetValue: 0, color: '#64748b', icon: 'inbox',
    };
    const appBuckets: Bucket[] = dbBuckets.some(b => b.id === 'ruong-chua')
      ? dbBuckets
      : [RUONG_CHUA, ...dbBuckets];

    const appClasses = dbClasses.length > 0
      ? dbClasses.map(dbRowToAssetClass)
      : DEFAULT_ASSET_CLASSES;

    const appAssets      = dbAssets.map(dbRowToAsset);
    const appLiabilities = dbLiabilities.map(row => dbRowToLiability(row, dbRepayments));
    const appRepayments  = dbRepayments.map(dbRowToRepayment);
    const appMoneyTxs    = dbMoneyTxs.map(dbRowToMoneyTx);

    // Tải asset_holdings (non-critical — bảng có thể chưa tồn tại nếu migration chưa chạy)
    let appHoldings: AssetHolding[] = [];
    try {
      const { data, error } = await supabase
        .from('asset_holdings')
        .select('*')
        .order('symbol', { ascending: true });
      if (error) {
        if (error.code !== '42P01') notifyError('đọc asset_holdings', error);
        // 42P01 = bảng chưa tồn tại → migration chưa chạy → im lặng
      } else {
        appHoldings = (data ?? []).map(dbRowToHolding);
      }
    } catch (e) { console.error('[Supabase] fetch asset_holdings exception:', e); }

    return {
      ...INITIAL_DATA,
      assets:            appAssets,
      buckets:           appBuckets,
      assetClasses:      appClasses,
      liabilities:       appLiabilities,
      repayments:        appRepayments,
      moneyTransactions: appMoneyTxs,
      holdings:          appHoldings,
    };
  } catch (e) {
    console.error('[Supabase] Kết nối thất bại:', e);
    return null;
  }
}

/** Lưu toàn bộ state lên DB. Trả về true nếu không có lỗi nào. */
export async function saveStateToCloud(state: AppState): Promise<boolean> {
  const [userId, workspaceId] = await Promise.all([
    getSessionUserId(),
    getWorkspaceId(),
  ]);

  if (!userId) {
    console.warn('[Supabase] Không có session — hủy lưu để tránh vi phạm RLS.');
    window.dispatchEvent(new CustomEvent('wealthos:sync-error', {
      detail: {
        source: 'saveState',
        message: 'Phiên làm việc hết hạn — không thể lưu dữ liệu.',
        details: 'Không tìm thấy session hợp lệ. Dữ liệu chưa được đồng bộ lên cloud.',
        hint:    'Vui lòng đăng xuất và đăng nhập lại để tiếp tục.',
        isAuthError: true,
      },
    }));
    return false;
  }

  if (!workspaceId) {
    // workspace_id null → mọi INSERT sẽ vi phạm RLS (workspace_id = my_workspace_id() sẽ fail).
    // Nguyên nhân: GRANT chưa được cấp trên bảng workspaces cho authenticated role.
    // Hướng dẫn: chạy scripts/repair_rls.sql trong Supabase SQL Editor.
    console.warn('[Supabase] Không lấy được workspace_id — bỏ qua lưu để tránh lỗi RLS.');
    window.dispatchEvent(new CustomEvent('wealthos:sync-error', {
      detail: {
        source: 'saveState',
        message: 'Chưa khởi tạo không gian làm việc — không thể lưu dữ liệu.',
        details: 'workspace_id = null: bảng workspaces chưa được cấp quyền cho authenticated role.',
        hint:    'Admin: chạy scripts/repair_rls.sql trong Supabase SQL Editor, sau đó tải lại trang.',
        isAuthError: false,
      },
    }));
    return false;
  }

  let hasErrors = false;

  try {
    // Round 1: master data (không có FK phụ thuộc)
    const r1 = await Promise.allSettled([
      syncFunds(state.buckets, userId, workspaceId),
      syncAssetClasses(state.assetClasses || [], userId, workspaceId),
      syncLiabilities(state.liabilities || [], userId, workspaceId),
    ]);
    r1.forEach(r => { if (r.status === 'rejected' || r.value === false) hasErrors = true; });

    // Round 2: dependent data
    const r2 = await Promise.allSettled([
      syncAssets(state.assets, userId, workspaceId),
      syncRepayments(state.repayments || [], userId, workspaceId),
    ]);
    r2.forEach(r => { if (r.status === 'rejected' || r.value === false) hasErrors = true; });
  } catch (e) {
    console.error('[Supabase] Lưu thất bại:', e);
    hasErrors = true;
  }

  if (!hasErrors) {
    console.log('[Supabase] ✓ Lưu thành công lúc', new Date().toLocaleTimeString('vi-VN'));
    window.dispatchEvent(new CustomEvent('wealthos:sync-ok'));
  }

  return !hasErrors;
}

async function syncAssets(assets: Asset[], userId: string | null, workspaceId: string | null): Promise<boolean> {
  // Loại tài sản đặc biệt in-memory (ví chung) — ID không phải UUID, không thể persist vào DB.
  // migrateState sẽ tái tạo chúng từ formula mỗi khi load.
  const syncable = assets.filter(a => a.id !== '__cash_wallet__');

  const { data: existing, error: fetchErr } = await supabase.from('assets').select('id');
  if (fetchErr) { notifyError('đọc assets ids', fetchErr); return false; }

  const dbIds    = new Set((existing ?? []).map((r: any) => String(r.id)));
  const stateIds = new Set(syncable.map(a => a.id));

  const toDelete = Array.from(dbIds).filter(id => !stateIds.has(id));
  if (toDelete.length > 0) {
    const { error } = await supabase.from('assets').delete().in('id', toDelete);
    if (error) { notifyError('xoá assets', error); return false; }
  }

  if (syncable.length > 0) {
    const payload = syncable.map(a => assetToDbRow(a, userId, workspaceId));
    const { error } = await supabase.from('assets').upsert(payload, { onConflict: 'id' });
    if (error) { notifyError('upsert assets', error); return false; }
  }
  return true;
}

async function syncFunds(buckets: Bucket[], userId: string | null, workspaceId: string | null): Promise<boolean> {
  // Chỉ sync quỹ có UUID — slug ID như 'ruong-chua' là shared PK, conflict cross-user gây lỗi RLS.
  // 'ruong-chua' luôn được thêm in-memory khi load (xem loadStateFromCloud), không cần persist.
  const syncable = buckets.filter(b => isUUID(b.id));
  if (syncable.length > 0) {
    const payload = syncable.map(b => bucketToDbRow(b, userId, workspaceId));
    const { error } = await supabase.from('funds').upsert(payload, { onConflict: 'id' });
    if (error) { notifyError('upsert funds', error); return false; }
  }
  return true;
}

/** Xoá một fund khỏi DB khi người dùng bấm xoá (gọi riêng, không dùng toDelete batch). */
export async function deleteFundFromCloud(fundId: string): Promise<boolean> {
  if (!fundId || fundId === 'ruong-chua') return true; // 'ruong-chua' không được xoá — là quỹ gốc
  const { error } = await supabase.from('funds').delete().eq('id', fundId);
  if (error) { notifyError('xoá fund', error); return false; }
  return true;
}

/**
 * Cập nhật metadata quỹ trực tiếp lên Supabase — multi-tenant safe.
 * Điều kiện WHERE: id = fundId AND user_id = userId
 * → chỉ owner của quỹ mới có thể cập nhật, RLS sẽ chặn nếu user_id không khớp.
 */
export async function updateFundMetadata(
  fundId: string,
  fields: { name?: string; description?: string; targetValue?: number },
  userId: string
): Promise<boolean> {
  const payload: Record<string, any> = {};
  if (fields.name        != null) payload.name         = fields.name;
  if (fields.description != null) payload.description  = fields.description;
  if (fields.targetValue != null) payload.target_value = fields.targetValue;

  const { error } = await supabase
    .from('funds')
    .update(payload)
    .eq('id',      fundId)   // chỉ đúng quỹ này
    .eq('user_id', userId);  // chỉ đúng owner — bảo mật multi-tenant

  if (error) { notifyError('cập nhật fund metadata', error); return false; }
  return true;
}

// ── SAA: Fund Targets ─────────────────────────────────────────────────────────

/** Tải danh sách target cho một quỹ từ bảng fund_targets. */
export async function loadFundTargets(fundId: string): Promise<FundTargetRow[]> {
  const { data, error } = await supabase
    .from('fund_targets')
    .select('*')
    .eq('fund_id', fundId);
  if (error) {
    console.error('[SAA] Tải fund_targets thất bại:', error);
    return [];
  }
  return (data ?? []).map((r: any) => ({
    fund_id:           String(r.fund_id),
    asset_class_id:    String(r.asset_class_id),
    target_weight:     Number(r.target_weight    ?? 0),
    target_value:      Number(r.target_value     ?? 0),
    expected_yield:    Number(r.expected_yield   ?? 0),
    expected_cap_gain: Number(r.expected_cap_gain ?? 0),
  }));
}

/**
 * Lưu hoạch định SAA cho một quỹ.
 * Logic: xóa hết targets cũ → insert mới (idempotent, tránh conflict).
 * Sau đó cập nhật target_yield và target_performance trên bảng funds.
 */
export async function saveFundTargets(
  fundId:       string,
  targets:      FundTargetRow[],
  userId:       string,
  blendedYield: number,   // % — weighted average yield
  blendedTotal: number,   // % — yield + cap gain
): Promise<boolean> {
  // Bước 1: Xóa targets cũ của quỹ này
  const { error: delErr } = await supabase
    .from('fund_targets')
    .delete()
    .eq('fund_id', fundId);
  if (delErr) { notifyError('xoá fund_targets cũ', delErr); return false; }

  // Bước 2: Insert targets mới (nếu có)
  const workspaceId = await getWorkspaceId();
  if (targets.length > 0) {
    const payload = targets.map(t => ({
      fund_id:           t.fund_id,
      asset_class_id:    t.asset_class_id,
      target_weight:     t.target_weight,
      target_value:      t.target_value,
      expected_yield:    t.expected_yield,
      expected_cap_gain: t.expected_cap_gain,
      user_id:           userId,
      workspace_id:      workspaceId,
    }));
    const { error: insErr } = await supabase.from('fund_targets').insert(payload);
    if (insErr) { notifyError('lưu fund_targets mới', insErr); return false; }
  }

  // Bước 3: Cập nhật target_yield và target_performance trên bảng funds
  // (cột có thể chưa tồn tại nếu migration chưa chạy — bỏ qua lỗi schema)
  try {
    await supabase
      .from('funds')
      .update({ target_yield: blendedYield, target_performance: blendedTotal })
      .eq('id',      fundId)
      .eq('user_id', userId);
  } catch (e) {
    console.warn('[SAA] Cột target_yield/target_performance có thể chưa có trong schema:', e);
  }

  console.log('[SAA] ✓ Lưu hoạch định thành công:', { fundId, rows: targets.length, blendedYield, blendedTotal });
  return true;
}

async function syncAssetClasses(classes: AssetClassMeta[], userId: string | null, workspaceId: string | null): Promise<boolean> {
  // Chỉ đồng bộ lớp tài sản có UUID hợp lệ (bỏ qua slug mặc định như "bds", "co-phieu")
  const syncable = classes.filter(c => isUUID(c.id));
  if (syncable.length === 0) return true;

  const { data: existing, error: fetchErr } = await supabase.from('asset_classes').select('id');
  if (fetchErr) { notifyError('đọc asset_classes ids', fetchErr); return false; }

  const dbIds    = new Set((existing ?? []).map((r: any) => String(r.id)));
  const stateIds = new Set(syncable.map(c => c.id));

  const toDelete = Array.from(dbIds).filter(id => !stateIds.has(id));
  if (toDelete.length > 0) {
    const { error } = await supabase.from('asset_classes').delete().in('id', toDelete);
    if (error) {
      if (error.code === '23503') {
        notifyError('xoá asset_classes', {
          ...error,
          message: 'Không thể xóa lớp tài sản đang có tài sản bên trong. Hãy di chuyển tài sản sang lớp khác trước.',
          hint: 'Vào Quản lý danh mục → chuyển tất cả tài sản sang lớp khác rồi thử lại.',
        });
      } else {
        notifyError('xoá asset_classes', error);
      }
      return false;
    }
  }

  const payload = syncable.map(c => assetClassToDbRow(c, userId, workspaceId));
  const { error } = await supabase.from('asset_classes').upsert(payload, { onConflict: 'id' });
  if (error) { notifyError('upsert asset_classes', error); return false; }
  return true;
}

async function syncLiabilities(liabilities: Liability[], userId: string | null, workspaceId: string | null): Promise<boolean> {
  const { data: existing, error: fetchErr } = await supabase.from('liabilities').select('id');
  if (fetchErr) { notifyError('đọc liabilities ids', fetchErr); return false; }

  const dbIds    = new Set((existing ?? []).map((r: any) => String(r.id)));
  const stateIds = new Set(liabilities.map(l => l.id));

  const toDelete = Array.from(dbIds).filter(id => !stateIds.has(id));
  if (toDelete.length > 0) {
    const { error } = await supabase.from('liabilities').delete().in('id', toDelete);
    if (error) { notifyError('xoá liabilities', error); return false; }
  }

  if (liabilities.length > 0) {
    const payload = liabilities.map(l => liabilityToDbRow(l, userId, workspaceId));
    const { error } = await supabase.from('liabilities').upsert(payload, { onConflict: 'id' });
    if (error) { notifyError('upsert liabilities', error); return false; }
  }
  return true;
}

async function syncRepayments(repayments: LiabilityRepayment[], userId: string | null, workspaceId: string | null): Promise<boolean> {
  const { data: existing, error: fetchErr } = await supabase.from('liability_repayments').select('id');
  if (fetchErr) { notifyError('đọc liability_repayments ids', fetchErr); return false; }

  const dbIds    = new Set((existing ?? []).map((r: any) => String(r.id)));
  const stateIds = new Set(repayments.map(r => r.id));

  const toDelete = Array.from(dbIds).filter(id => !stateIds.has(id));
  if (toDelete.length > 0) {
    const { error } = await supabase.from('liability_repayments').delete().in('id', toDelete);
    if (error) { notifyError('xoá liability_repayments', error); return false; }
  }

  if (repayments.length > 0) {
    const payload = repayments.map(r => repaymentToDbRow(r, userId, workspaceId));
    const { error } = await supabase.from('liability_repayments').upsert(payload, { onConflict: 'id' });
    if (error) { notifyError('upsert liability_repayments', error); return false; }
  }
  return true;
}

// ── Money Transactions CRUD ───────────────────────────────────────────────────
// Dùng direct INSERT/DELETE thay vì bulk-sync để tránh re-upload toàn bộ history.

function dbRowToMoneyTx(row: any): MoneyTransaction {
  return {
    id:           String(row.id),
    user_id:      row.user_id      ? String(row.user_id)      : undefined,
    workspace_id: row.workspace_id ? String(row.workspace_id) : undefined,
    type:         row.type         as MoneyTransaction['type'],
    asset_id:     row.asset_id     ? String(row.asset_id)     : undefined,
    asset_name:   row.asset_name   ? String(row.asset_name)   : undefined,
    quantity:     row.quantity     != null ? Number(row.quantity)     : undefined,
    price:        row.price        != null ? Number(row.price)        : undefined,
    gross_amount: row.gross_amount != null ? Number(row.gross_amount) : undefined,
    fees:         row.fees         != null ? Number(row.fees)         : undefined,
    net_amount:   Number(row.net_amount ?? 0),
    fund_id:      row.fund_id      ? String(row.fund_id)      : undefined,
    dest_fund_id: row.dest_fund_id ? String(row.dest_fund_id) : undefined,
    reason:       row.reason       ?? '',
    note:         row.note         ? String(row.note)         : undefined,
    date:         row.date         ?? new Date().toISOString().split('T')[0],
    created_at:   row.created_at   ?? undefined,
  };
}

/** Tải toàn bộ giao dịch tiền của workspace hiện tại (dùng trong loadStateFromCloud). */
export async function loadMoneyTransactionsFromCloud(): Promise<MoneyTransaction[]> {
  try {
    const { data, error } = await supabase
      .from('transactions')
      .select('*')
      .order('date', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(500);   // giới hạn 500 giao dịch gần nhất để tránh quá tải
    if (error) {
      const isMissing = error.code === '42P01' || error.code === 'PGRST200'
        || (error.message ?? '').toLowerCase().includes("transactions");
      if (isMissing) {
        console.warn('[MoneyTx] Bảng transactions chưa tồn tại — hãy chạy migration_transactions.sql');
      } else {
        notifyError('đọc transactions', error);
      }
      return [];
    }
    return (data ?? []).map(dbRowToMoneyTx);
  } catch (e) {
    console.error('[MoneyTx] Lỗi tải giao dịch:', e);
    return [];
  }
}

/** Thêm một giao dịch trực tiếp vào DB (không qua bulk sync). */
export async function addMoneyTransactionToCloud(tx: MoneyTransaction): Promise<boolean> {
  const [userId, workspaceId] = await Promise.all([getSessionUserId(), getWorkspaceId()]);
  if (!userId || !workspaceId) {
    console.warn('[MoneyTx] Không có session hoặc workspace — hủy thêm giao dịch.');
    return false;
  }
  const { error } = await supabase.from('transactions').insert({
    id:           tx.id,
    user_id:      userId,
    workspace_id: workspaceId,
    type:         tx.type,
    asset_id:     tx.asset_id     ?? null,
    asset_name:   tx.asset_name   ?? null,
    quantity:     tx.quantity     ?? null,
    price:        tx.price        ?? null,
    gross_amount: tx.gross_amount != null ? Math.round(tx.gross_amount) : null,
    fees:         tx.fees         != null ? Math.round(tx.fees)         : null,
    net_amount:   Math.round(tx.net_amount),
    fund_id:      tx.fund_id      ?? null,
    dest_fund_id: tx.dest_fund_id ?? null,
    reason:       tx.reason       || '',
    note:         tx.note         || '',
    date:         tx.date,
  });
  if (error) { notifyError('thêm giao dịch', error); return false; }
  return true;
}

/** Cập nhật một giao dịch trực tiếp trong DB. */
export async function updateMoneyTransactionInCloud(tx: MoneyTransaction): Promise<boolean> {
  const { error } = await supabase.from('transactions').update({
    type:         tx.type,
    asset_id:     tx.asset_id     ?? null,
    asset_name:   tx.asset_name   ?? null,
    quantity:     tx.quantity     ?? null,
    price:        tx.price        ?? null,
    gross_amount: tx.gross_amount != null ? Math.round(tx.gross_amount) : null,
    fees:         tx.fees         != null ? Math.round(tx.fees)         : null,
    net_amount:   Math.round(tx.net_amount),
    fund_id:      tx.fund_id      ?? null,
    dest_fund_id: tx.dest_fund_id ?? null,
    reason:       tx.reason       || '',
    note:         tx.note         || '',
    date:         tx.date,
  }).eq('id', tx.id);
  if (error) { notifyError('cập nhật giao dịch', error); return false; }
  return true;
}

/** Xoá một giao dịch trực tiếp khỏi DB. */
export async function deleteMoneyTransactionFromCloud(id: string): Promise<boolean> {
  const { error } = await supabase.from('transactions').delete().eq('id', id);
  if (error) { notifyError('xoá giao dịch', error); return false; }
  return true;
}

// ── File export / import ───────────────────────────────────────────────────────

export function exportState(state: AppState): void {
  const json = JSON.stringify(state, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `wealthos-backup-${new Date().toISOString().split('T')[0]}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

export function importState(file: File, onSuccess: (state: AppState) => void): void {
  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const state = JSON.parse(e.target?.result as string) as AppState;
      onSuccess(state);
    } catch {
      alert('File không hợp lệ');
    }
  };
  reader.readAsText(file);
}
